// Turns a raw stage status into words. The only place in the app that does, so
// the mapping can be reviewed and tested in one file.
//
// The status field has a different name and a different set of values in each
// stage, so every value is handled here rather than assumed.

const NO_CHIP = null;

// ASSESSED and READY deliberately produce no chip. Silence is the signal that
// nothing is wrong, and a badge on every section would make the ones that do
// carry a caveat look worse by contrast.
const CHIPS = {
  ASSESSED: NO_CHIP,
  READY: NO_CHIP,
  NO_REPAIR_NEEDED: {
    chip: 'No repair needed',
    tone: 'good',
    explainer: null,
  },
  // Not surfaced. The status is still returned by the assessor and stored, but
  // a student already knows the marking is automated, so a caveat on top of
  // that told them nothing they could act on. Decided by Louis G on 18
  // September; bring it back with a dedicated writing task app, where there is
  // somewhere for the uncertainty to lead.
  PROVISIONAL: NO_CHIP,
  INSUFFICIENT_EVIDENCE: {
    chip: 'Limited evidence',
    tone: 'warn',
    explainer: "There wasn't enough written here to assess properly. Answering all "
      + 'three parts at length will give you a much fuller picture.',
  },
  INPUT_RESOLUTION_FAILED: {
    chip: "Couldn't be completed",
    tone: 'warn',
    explainer: 'Something went wrong at our end, so this didn’t run. '
      + "It's nothing to do with what you wrote.",
  },
};

export function taraStageStatusChip(rawStatus) {
  if (!rawStatus) return NO_CHIP;
  const key = String(rawStatus).trim().toUpperCase();
  if (!(key in CHIPS)) return NO_CHIP;
  return CHIPS[key];
}

// The completion scale on each of the three writing tasks. Absent is neutral
// grey, not red: a task the student did not attempt is information, not an
// alarm, and there is no red token in the workspace palette.
const TASK_CHIPS = {
  SECURE: { label: 'Done well', tone: 'good' },
  PARTIAL: { label: 'Partly done', tone: 'warn' },
  ABSENT: { label: 'Not done', tone: 'muted' },
  NOT_ASSESSED: { label: 'Not assessed', tone: 'faint' },
};

export function taraTaskStatusChip(rawStatus) {
  const key = String(rawStatus || '').trim().toUpperCase();
  return TASK_CHIPS[key] || TASK_CHIPS.NOT_ASSESSED;
}

// Eight traits in rubric order, with the wording the contract uses. Used only
// when a stage omits the label, so a null never leaves a blank row.
export const TARA_TRAIT_LABELS = {
  interpretive_control: 'Reading the statement',
  conceptual_distinction: 'Drawing distinctions',
  argument_against: 'Arguing the other side',
  warrant_and_inference: 'Backing up claims',
  dialectical_integration: 'Weighing both sides',
  conditionality_and_scope: 'Saying when it holds',
  final_judgement: 'Reaching a judgement',
  communication_and_structure: 'Structure and clarity',
};

export function taraTraitLabel(trait, label) {
  if (label) return label;
  return TARA_TRAIT_LABELS[String(trait || '').trim().toLowerCase()] || null;
}


// Where a finding's evidence came from, in words.
//
// Section 13 of prompt 1 defines three: TEXTUAL, WHOLE_RESPONSE_REVIEW and
// NOT_ASSESSABLE. They were being printed raw, so a student read
// "Based on: WHOLE_RESPONSE_REVIEW".
//
// NOT_ASSESSABLE returns null: telling a student a finding could not be
// evidenced is not a caption, it is the absence of a finding.
const EVIDENCE_BASIS = {
  TEXTUAL: 'specific sentences in your essay',
  WHOLE_RESPONSE_REVIEW: 'a reading of your whole essay',
  NOT_ASSESSABLE: null,
};

export function taraEvidenceBasisLabel(basis) {
  if (!basis || typeof basis !== 'string') return null;
  const known = EVIDENCE_BASIS[basis.trim().toUpperCase()];
  if (known !== undefined) return known;
  // An unrecognised value is not shown. A new enum reaching a student reads as
  // a bug to them and tells them nothing either way.
  return null;
}
